import React, { useState, useEffect, useCallback } from 'react';
import {
    Mail, Calendar, FileText, StickyNote, FolderOpen,
    Play, Shield, CheckCircle, XCircle, Zap, Loader2,
    Clock, Settings, History, Plus, ChevronRight,
    ToggleLeft, ToggleRight, AlertTriangle, Eye,
    X, ArrowRight, Check, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';
import { api } from '../lib/api';
import { replicaService } from '../lib/services';

// ============================================================================
// Types
// ============================================================================

interface WorkflowTemplate {
    id: string;
    slug: string;
    title: string;
    description: string;
    category: string;
    riskLevel: string;
    requiresApprovalDefault: boolean;
    integrationsRequired: string[];
    scopesRequired: string;
    inputSchema: string;
    steps: string;
    outputSchema: string;
    isActive: boolean;
    icon: string;
    badgeLabel?: string;
    badgeColor?: string;
}

interface WorkflowPolicy {
    approvalRequired: boolean;
    allowWrite: boolean;
    allowSend: boolean;
    timeWindowDays: number;
}

interface UserWorkflow {
    id: string;
    userId: string;
    replicaId: string;
    templateId: string;
    name: string;
    isEnabled: boolean;
    config: string;
    mode: string;
    schedule: string | null;
    trigger: string | null;
    policy: string;
    lastRunAt: string | null;
    template: WorkflowTemplate;
    lastRun?: WorkflowRun | null;
    integrationStatus?: { name: string; connected: boolean }[];
}

interface WorkflowArtifact {
    id: string;
    runId: string;
    type: string;
    title: string;
    content: string;
    createdAt: string;
}

interface WorkflowRun {
    id: string;
    userWorkflowId: string;
    status: string;
    startedAt: string | null;
    endedAt: string | null;
    outputs: string;
    logs: string;
    error: string | null;
    artifacts: WorkflowArtifact[];
    createdAt: string;
}

// ============================================================================
// Helpers
// ============================================================================

const ICON_MAP: Record<string, React.ElementType> = {
    Mail, Calendar, FileText, StickyNote, FolderOpen, Zap,
};

function getIcon(name: string): React.ElementType {
    return ICON_MAP[name] || Zap;
}

const RISK_STYLES: Record<string, string> = {
    low: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    medium: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    high: 'text-red-400 bg-red-500/10 border-red-500/20',
};

const BADGE_COLORS: Record<string, string> = {
    blue: 'bg-hig-accent/10 text-hig-accent border-hig-accent/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

const STATUS_STYLES: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
    queued: { label: 'Queued', color: 'text-slate-400', Icon: Clock },
    running: { label: 'Running…', color: 'text-hig-accent', Icon: Loader2 },
    needs_approval: { label: 'Needs review', color: 'text-amber-400', Icon: Shield },
    completed: { label: 'Completed', color: 'text-emerald-400', Icon: CheckCircle },
    failed: { label: 'Failed', color: 'text-red-400', Icon: XCircle },
    canceled: { label: 'Canceled', color: 'text-slate-500', Icon: XCircle },
};

const MODE_LABELS: Record<string, string> = {
    manual: 'Manual',
    scheduled: 'Scheduled',
    triggered: 'Triggered',
};

function formatDate(d: string | null): string {
    if (!d) return 'Never';
    return new Date(d).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

function policyLabel(policy: WorkflowPolicy): string {
    if (policy.approvalRequired && !policy.allowWrite) return 'Read-only';
    if (policy.approvalRequired && !policy.allowSend) return 'Draft only';
    if (policy.approvalRequired) return 'Review before actions';
    return 'Autonomous';
}

// ============================================================================
// Props
// ============================================================================

interface WorkflowsPageProps {
    onNavigate: (view: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function WorkflowsPage({ onNavigate }: WorkflowsPageProps) {
    const { user } = useAuth();
    const { userReplicas: contextReplicas } = useReplica();

    // Fetch replicas from backend API (source of truth) with fallback to context
    const [backendReplicas, setBackendReplicas] = useState<{ id: string; name: string; category: string }[]>([]);

    useEffect(() => {
        if (!user?.uid) return;
        replicaService.getUserReplicas(user.uid).then(res => {
            if (res.data?.replicas && res.data.replicas.length > 0) {
                setBackendReplicas(res.data.replicas.map((r: any) => ({
                    id: r.id || '',
                    name: r.name || 'Unnamed',
                    category: r.category || '',
                })));
            }
        }).catch(err => {
            console.warn('Failed to fetch replicas from backend:', err);
        });
    }, [user?.uid]);

    // Merge: backend replicas take priority, then add context-only replicas
    const contextMapped = (contextReplicas || []).map((r: any) => ({
        id: r.id || '', name: r.name || 'Unnamed', category: r.category || '',
    }));
    const backendIds = new Set(backendReplicas.map(r => r.id));
    const replicas = [
        ...backendReplicas,
        ...contextMapped.filter((r: any) => !backendIds.has(r.id)),
    ];

    // State
    const [selectedReplicaId, setSelectedReplicaId] = useState('');
    const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
    const [workflows, setWorkflows] = useState<UserWorkflow[]>([]);
    const [loading, setLoading] = useState(true);
    const [runningWorkflows, setRunningWorkflows] = useState<Set<string>>(new Set());

    // Modal state
    const [enableModal, setEnableModal] = useState<{ open: boolean; template: WorkflowTemplate | null; step: number }>({
        open: false, template: null, step: 1,
    });
    const [modalPolicy, setModalPolicy] = useState<WorkflowPolicy>({
        approvalRequired: true, allowWrite: false, allowSend: false, timeWindowDays: 7,
    });
    const [modalConfig, setModalConfig] = useState<Record<string, any>>({});
    const [modalRunResult, setModalRunResult] = useState<WorkflowRun | null>(null);

    // History panel
    const [historyPanel, setHistoryPanel] = useState<{ open: boolean; workflowId: string | null }>({
        open: false, workflowId: null,
    });
    const [historyRuns, setHistoryRuns] = useState<WorkflowRun[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Settings panel
    const [settingsPanel, setSettingsPanel] = useState<{ open: boolean; workflow: UserWorkflow | null }>({
        open: false, workflow: null,
    });

    // Auto-select first replica when replicas become available
    useEffect(() => {
        if (!selectedReplicaId && replicas.length > 0) {
            setSelectedReplicaId(replicas[0].id);
        }
    }, [replicas, selectedReplicaId]);

    // ─── Data Loading ──────────────────────────────────────

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [tplRes, wfRes] = await Promise.all([
                api.get<WorkflowTemplate[]>('/workflows/workflow-templates'),
                selectedReplicaId
                    ? api.get<UserWorkflow[]>('/workflows/user-workflows', { replicaId: selectedReplicaId })
                    : Promise.resolve({ data: [] as UserWorkflow[] }),
            ]);
            if (tplRes.data) setTemplates(tplRes.data);
            if (wfRes.data) setWorkflows(wfRes.data);
        } catch (err) {
            console.error('Failed to load workflows data:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedReplicaId]);

    useEffect(() => { loadData(); }, [loadData]);

    // ─── Actions ───────────────────────────────────────────

    const runWorkflow = useCallback(async (workflowId: string) => {
        setRunningWorkflows(prev => new Set(prev).add(workflowId));
        try {
            await api.post(`/workflows/user-workflows/${workflowId}/run`);
            await loadData();
        } catch (err: any) {
            console.error('Run failed:', err);
        } finally {
            setRunningWorkflows(prev => { const n = new Set(prev); n.delete(workflowId); return n; });
        }
    }, [loadData]);

    const disableWorkflow = useCallback(async (workflowId: string) => {
        await api.post(`/workflows/user-workflows/${workflowId}/disable`);
        await loadData();
    }, [loadData]);

    const loadHistory = useCallback(async (workflowId: string) => {
        setHistoryLoading(true);
        try {
            const res = await api.get<WorkflowRun[]>('/workflows/workflow-runs', { userWorkflowId: workflowId });
            if (res.data) setHistoryRuns(res.data);
        } catch (err) {
            console.error('Failed to load history:', err);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    // ─── Enable Modal Logic ────────────────────────────────

    const openEnableModal = (template: WorkflowTemplate) => {
        setEnableModal({ open: true, template, step: 1 });
        setModalPolicy({
            approvalRequired: template.requiresApprovalDefault,
            allowWrite: false,
            allowSend: false,
            timeWindowDays: 7,
        });
        // Parse inputSchema and set defaults
        try {
            const schema = JSON.parse(template.inputSchema);
            const defaults: Record<string, any> = {};
            if (schema.properties) {
                for (const [key, prop] of Object.entries(schema.properties) as [string, any][]) {
                    if (prop.default !== undefined) defaults[key] = prop.default;
                }
            }
            setModalConfig(defaults);
        } catch { setModalConfig({}); }
        setModalRunResult(null);
    };

    const enableWorkflow = async () => {
        if (!enableModal.template || !selectedReplicaId) return;
        try {
            const res = await api.post<UserWorkflow>('/workflows/user-workflows', {
                templateId: enableModal.template.id,
                replicaId: selectedReplicaId,
                config: modalConfig,
                policy: modalPolicy,
            });
            if (res.data) {
                // Step 4: First run
                setEnableModal(prev => ({ ...prev, step: 4 }));
                const runRes = await api.post<WorkflowRun>(`/workflows/user-workflows/${res.data.id}/run`);
                if (runRes.data) setModalRunResult(runRes.data);
                await loadData();
            }
        } catch (err: any) {
            console.error('Enable workflow failed:', err);
        }
    };

    const updateWorkflowSettings = async () => {
        if (!settingsPanel.workflow) return;
        try {
            await api.patch(`/workflows/user-workflows/${settingsPanel.workflow.id}`, {
                policy: JSON.parse(settingsPanel.workflow.policy),
                mode: settingsPanel.workflow.mode,
                schedule: settingsPanel.workflow.schedule,
                name: settingsPanel.workflow.name,
            });
            setSettingsPanel({ open: false, workflow: null });
            await loadData();
        } catch (err: any) {
            console.error('Update failed:', err);
        }
    };

    // ─── Derived ───────────────────────────────────────────

    const enabledWorkflows = workflows.filter(w => w.isEnabled);
    const enabledTemplateIds = new Set(workflows.map(w => w.templateId));

    const categories = [...new Set(templates.map(t => t.category))];

    // ─── Render ────────────────────────────────────────────

    if (loading && templates.length === 0) {
        return (
            <div className="max-w-5xl mx-auto flex items-center justify-center py-32">
                <Loader2 className="animate-spin text-hig-accent" size={28} />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto animate-fade-in">
            {/* ── Header — Deploy Mode ── */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-xl bg-hig-accent/10 flex items-center justify-center">
                            <Zap size={20} className="text-hig-accent" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-hig-text-primary">Active Mandates</h1>
                            <p className="text-xs text-hig-text-tertiary uppercase tracking-widest font-bold">Deploy Mode</p>
                        </div>
                    </div>
                    <p className="text-sm text-hig-text-secondary mt-2">
                        Delegate missions, monitor executions, and govern your Replica's operational scope
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onNavigate('work-mode')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-all"
                    >
                        <Zap size={14} /> Work Mode
                    </button>
                    <button
                        onClick={() => onNavigate('daily-routines')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all"
                    >
                        <Clock size={14} /> Daily Routines
                    </button>
                    <button
                        onClick={() => onNavigate('messages')}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary transition-all"
                    >
                        Messages
                    </button>
                    <button
                        onClick={() => loadData()}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary transition-all"
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>
            </div>

            {/* ── Replica Selector ── */}
            <div className="glass p-4 mb-6">
                <div className="flex items-center gap-4">
                    <label className="text-xs font-medium text-hig-text-secondary uppercase tracking-wide whitespace-nowrap">Active Replica</label>
                    {replicas.length > 0 ? (
                        <div className="flex items-center gap-2 overflow-x-auto">
                            {replicas.map((r: any) => (
                                <button
                                    key={r.id}
                                    onClick={() => setSelectedReplicaId(r.id)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap ${selectedReplicaId === r.id
                                        ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                                        : 'bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary'
                                        }`}
                                >
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold">
                                        {r.name?.[0] || '?'}
                                    </div>
                                    {r.name}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-hig-text-tertiary">No replicas yet</span>
                            <button onClick={() => onNavigate('create-replica')} className="text-sm text-hig-accent hover:underline">
                                Create one →
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ═════════════════════════════════════════ */}
            {/* SECTION 1 — Active Mandates              */}
            {/* ═════════════════════════════════════════ */}

            <div className="mb-10">
                <h2 className="text-sm font-semibold text-hig-text-secondary uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Zap size={14} /> Active Mandates
                    {enabledWorkflows.length > 0 && (
                        <span className="text-[10px] bg-hig-accent/10 text-hig-accent px-1.5 py-0.5 rounded-full">
                            {enabledWorkflows.length}
                        </span>
                    )}
                </h2>

                {enabledWorkflows.length === 0 ? (
                    <div className="glass p-8 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-hig-accent/10 flex items-center justify-center mx-auto mb-4">
                            <Plus size={24} className="text-hig-accent" />
                        </div>
                        <h3 className="text-lg font-semibold text-hig-text-primary mb-2">Delegate your first mission</h3>
                        <p className="text-sm text-hig-text-secondary mb-4 max-w-md mx-auto">
                            Choose from mandate templates below to begin delegating governed tasks to your Replica.
                        </p>
                        <button
                            onClick={() => document.getElementById('templates-section')?.scrollIntoView({ behavior: 'smooth' })}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold transition-all"
                        >
                            Delegate a Mission <ChevronRight size={14} />
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {enabledWorkflows.map(wf => {
                            const policy: WorkflowPolicy = JSON.parse(wf.policy || '{}');
                            const Icon = getIcon(wf.template.icon);
                            const isRunning = runningWorkflows.has(wf.id);
                            const lastRunStatus = wf.lastRun?.status;

                            return (
                                <div key={wf.id} className="glass overflow-hidden">
                                    <div className="p-4">
                                        <div className="flex items-start gap-3">
                                            {/* Icon */}
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${lastRunStatus === 'completed' ? 'bg-emerald-500/10' :
                                                lastRunStatus === 'failed' ? 'bg-red-500/10' :
                                                    'bg-hig-accent/10'
                                                }`}>
                                                <Icon size={18} className={
                                                    lastRunStatus === 'completed' ? 'text-emerald-400' :
                                                        lastRunStatus === 'failed' ? 'text-red-400' :
                                                            'text-hig-accent'
                                                } />
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-medium text-hig-text-primary">{wf.name}</h3>
                                                <p className="text-xs text-hig-text-secondary mt-0.5">{wf.template.description}</p>
                                                <div className="flex items-center flex-wrap gap-2 mt-2">
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-hig-accent/10 text-hig-accent border border-hig-accent/20">
                                                        {MODE_LABELS[wf.mode] || wf.mode}
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${RISK_STYLES[wf.template.riskLevel] || ''}`}>
                                                        {wf.template.riskLevel}
                                                    </span>
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-hig-bg-tertiary text-hig-text-tertiary flex items-center gap-0.5">
                                                        <Shield size={8} /> {policyLabel(policy)}
                                                    </span>
                                                    {wf.lastRunAt && (
                                                        <span className="text-[10px] text-hig-text-tertiary flex items-center gap-0.5">
                                                            <Clock size={8} /> Last: {formatDate(wf.lastRunAt)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <button
                                                    onClick={() => runWorkflow(wf.id)}
                                                    disabled={isRunning}
                                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${isRunning
                                                        ? 'bg-hig-accent/20 text-hig-accent cursor-wait'
                                                        : 'bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold'
                                                        }`}
                                                >
                                                    {isRunning ? (
                                                        <><Loader2 size={12} className="animate-spin" /> Running</>
                                                    ) : (
                                                        <><Play size={12} /> Run</>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => { setHistoryPanel({ open: true, workflowId: wf.id }); loadHistory(wf.id); }}
                                                    className="p-2 rounded-lg text-hig-text-tertiary hover:text-hig-text-primary bg-hig-bg-tertiary hover:bg-hig-bg-secondary transition-all"
                                                    title="History"
                                                >
                                                    <History size={14} />
                                                </button>
                                                <button
                                                    onClick={() => setSettingsPanel({ open: true, workflow: { ...wf } })}
                                                    className="p-2 rounded-lg text-hig-text-tertiary hover:text-hig-text-primary bg-hig-bg-tertiary hover:bg-hig-bg-secondary transition-all"
                                                    title="Settings"
                                                >
                                                    <Settings size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Last run result */}
                                    {wf.lastRun && wf.lastRun.status && (
                                        <div className={`border-t border-hig-border px-4 py-2.5 flex items-center justify-between ${wf.lastRun.status === 'completed' ? 'bg-emerald-500/5' :
                                            wf.lastRun.status === 'failed' ? 'bg-red-500/5' :
                                                wf.lastRun.status === 'needs_approval' ? 'bg-amber-500/5' : 'bg-hig-bg-tertiary/50'
                                            }`}>
                                            {(() => {
                                                const s = STATUS_STYLES[wf.lastRun.status];
                                                const SIcon = s?.Icon || Clock;
                                                return (
                                                    <span className={`flex items-center gap-1.5 text-xs ${s?.color || 'text-hig-text-tertiary'}`}>
                                                        <SIcon size={12} className={wf.lastRun.status === 'running' ? 'animate-spin' : ''} />
                                                        {s?.label || wf.lastRun.status} • {formatDate(wf.lastRun.createdAt)}
                                                    </span>
                                                );
                                            })()}
                                            {wf.lastRun.artifacts && wf.lastRun.artifacts.length > 0 && (
                                                <span className="text-[10px] text-hig-accent">
                                                    {wf.lastRun.artifacts.length} artifact{wf.lastRun.artifacts.length > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ═════════════════════════════════════════ */}
            {/* SECTION 2 — Mandate Templates            */}
            {/* ═════════════════════════════════════════ */}

            <div id="templates-section" className="mb-8">
                <h2 className="text-sm font-semibold text-hig-text-secondary uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FileText size={14} /> Mandate Templates
                    <span className="text-[10px] bg-hig-bg-tertiary text-hig-text-tertiary px-1.5 py-0.5 rounded-full">
                        {templates.length}
                    </span>
                </h2>

                {categories.map(cat => (
                    <div key={cat} className="mb-6">
                        <h3 className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wider mb-2 capitalize">{cat}</h3>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {templates.filter(t => t.category === cat).map(template => {
                                const Icon = getIcon(template.icon);
                                const isEnabled = enabledTemplateIds.has(template.id);

                                return (
                                    <div key={template.id} className="glass p-4 hover:border-hig-accent/20 transition-all">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-hig-accent/10 flex items-center justify-center flex-shrink-0">
                                                <Icon size={18} className="text-hig-accent" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-sm font-medium text-hig-text-primary">{template.title}</h3>
                                                    {template.badgeLabel && (
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${BADGE_COLORS[template.badgeColor || 'blue'] || BADGE_COLORS.blue}`}>
                                                            {template.badgeLabel}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-hig-text-secondary mt-1 line-clamp-2">{template.description}</p>
                                                <div className="flex items-center flex-wrap gap-1.5 mt-2">
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${RISK_STYLES[template.riskLevel] || ''}`}>
                                                        {template.riskLevel} risk
                                                    </span>
                                                    {template.integrationsRequired.map(integration => (
                                                        <span key={integration} className="text-[10px] px-1.5 py-0.5 rounded bg-hig-bg-tertiary text-hig-text-tertiary capitalize">
                                                            {integration}
                                                        </span>
                                                    ))}
                                                    {template.requiresApprovalDefault && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 flex items-center gap-0.5">
                                                            <Shield size={8} /> Review required
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0">
                                                {isEnabled ? (
                                                    <span className="flex items-center gap-1 text-xs text-emerald-400 px-3 py-2">
                                                        <CheckCircle size={12} /> Enabled
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => openEnableModal(template)}
                                                        disabled={!selectedReplicaId}
                                                        className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold transition-all disabled:opacity-40"
                                                    >
                                                        <Plus size={12} /> Enable
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Governance Footer ── */}
            <div className="flex items-start gap-2 p-4 rounded-lg bg-hig-bg-tertiary border border-hig-border mt-4 mb-8">
                <Shield size={16} className="text-hig-text-tertiary flex-shrink-0 mt-0.5" />
                <p className="text-xs text-hig-text-secondary">
                    All mandate executions are protected by the <strong className="text-hig-text-primary">Decision Guardian™</strong> and recorded in the immutable audit chain.
                    Every execution, escalation, and output is fully auditable.
                </p>
            </div>

            {/* ═════════════════════════════════════════ */}
            {/* Enable Workflow Modal                    */}
            {/* ═════════════════════════════════════════ */}

            {enableModal.open && enableModal.template && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-hig-bg-primary border border-hig-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-hig-bg-primary border-b border-hig-border p-4 rounded-t-2xl flex items-center justify-between z-10">
                            <div>
                                <h2 className="text-lg font-semibold text-hig-text-primary">Delegate Mandate</h2>
                                <p className="text-xs text-hig-text-secondary">{enableModal.template.title}</p>
                            </div>
                            <button onClick={() => setEnableModal({ open: false, template: null, step: 1 })} className="p-1 rounded-lg hover:bg-hig-bg-tertiary">
                                <X size={18} className="text-hig-text-tertiary" />
                            </button>
                        </div>

                        {/* Step Indicator */}
                        <div className="px-4 pt-4">
                            <div className="flex items-center gap-1 mb-4">
                                {[1, 2, 3, 4].map(s => (
                                    <div key={s} className={`flex-1 h-1 rounded-full ${s <= enableModal.step ? 'bg-hig-accent' : 'bg-hig-bg-tertiary'}`} />
                                ))}
                            </div>
                        </div>

                        <div className="p-4 pt-0">
                            {/* Step 1: Integrations */}
                            {enableModal.step === 1 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-hig-text-primary mb-1">Connect Integrations</h3>
                                    <p className="text-xs text-hig-text-secondary mb-4">
                                        This workflow requires the following integrations:
                                    </p>
                                    <div className="space-y-2">
                                        {enableModal.template.integrationsRequired.map(integration => (
                                            <div key={integration} className="flex items-center justify-between p-3 rounded-lg bg-hig-bg-tertiary">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-lg bg-hig-accent/10 flex items-center justify-center">
                                                        {integration === 'gmail' ? <Mail size={16} className="text-hig-accent" /> :
                                                            integration === 'gcal' ? <Calendar size={16} className="text-hig-accent" /> :
                                                                <FolderOpen size={16} className="text-hig-accent" />}
                                                    </div>
                                                    <span className="text-sm text-hig-text-primary capitalize">{integration === 'gcal' ? 'Google Calendar' : integration}</span>
                                                </div>
                                                <button
                                                    onClick={() => onNavigate('integrations')}
                                                    className="text-xs text-hig-accent hover:underline flex items-center gap-1"
                                                >
                                                    Connect <ArrowRight size={10} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 mt-3">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-xs text-amber-400">
                                                For this demo, integrations are simulated. The workflow will produce sample outputs.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setEnableModal(prev => ({ ...prev, step: 2 }))}
                                        className="w-full mt-4 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold transition-all"
                                    >
                                        Continue <ArrowRight size={14} />
                                    </button>
                                </div>
                            )}

                            {/* Step 2: Boundaries / Policy */}
                            {enableModal.step === 2 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-hig-text-primary mb-1">Set Boundaries</h3>
                                    <p className="text-xs text-hig-text-secondary mb-4">
                                        Control what your Replica can do, in human-friendly terms.
                                    </p>
                                    <div className="space-y-3">
                                        {/* Review before actions */}
                                        <label className="flex items-center justify-between p-3 rounded-lg bg-hig-bg-tertiary cursor-pointer">
                                            <div>
                                                <p className="text-sm text-hig-text-primary">Review before actions</p>
                                                <p className="text-xs text-hig-text-secondary">Your Replica will pause for your approval before taking action</p>
                                            </div>
                                            <button
                                                onClick={() => setModalPolicy(p => ({ ...p, approvalRequired: !p.approvalRequired }))}
                                                className="flex-shrink-0"
                                            >
                                                {modalPolicy.approvalRequired
                                                    ? <ToggleRight size={28} className="text-hig-accent" />
                                                    : <ToggleLeft size={28} className="text-hig-text-tertiary" />
                                                }
                                            </button>
                                        </label>

                                        {/* Allow writing drafts */}
                                        <label className="flex items-center justify-between p-3 rounded-lg bg-hig-bg-tertiary cursor-pointer">
                                            <div>
                                                <p className="text-sm text-hig-text-primary">Allow writing drafts</p>
                                                <p className="text-xs text-hig-text-secondary">Your Replica can create draft documents and emails</p>
                                            </div>
                                            <button
                                                onClick={() => setModalPolicy(p => ({ ...p, allowWrite: !p.allowWrite }))}
                                                className="flex-shrink-0"
                                            >
                                                {modalPolicy.allowWrite
                                                    ? <ToggleRight size={28} className="text-hig-accent" />
                                                    : <ToggleLeft size={28} className="text-hig-text-tertiary" />
                                                }
                                            </button>
                                        </label>

                                        {/* Allow sending */}
                                        <label className="flex items-center justify-between p-3 rounded-lg bg-hig-bg-tertiary cursor-pointer">
                                            <div>
                                                <p className="text-sm text-hig-text-primary">Allow sending automatically</p>
                                                <p className="text-xs text-hig-text-secondary">Your Replica can send emails without your review</p>
                                            </div>
                                            <button
                                                onClick={() => setModalPolicy(p => ({ ...p, allowSend: !p.allowSend }))}
                                                className="flex-shrink-0"
                                            >
                                                {modalPolicy.allowSend
                                                    ? <ToggleRight size={28} className="text-amber-400" />
                                                    : <ToggleLeft size={28} className="text-hig-text-tertiary" />
                                                }
                                            </button>
                                        </label>

                                        {/* Time window */}
                                        <div className="p-3 rounded-lg bg-hig-bg-tertiary">
                                            <p className="text-sm text-hig-text-primary mb-2">Time range limit</p>
                                            <div className="flex gap-2">
                                                {[3, 7, 14, 30].map(days => (
                                                    <button
                                                        key={days}
                                                        onClick={() => setModalPolicy(p => ({ ...p, timeWindowDays: days }))}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${modalPolicy.timeWindowDays === days
                                                            ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                                                            : 'bg-hig-bg-secondary text-hig-text-secondary'
                                                            }`}
                                                    >
                                                        Last {days} days
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setEnableModal(prev => ({ ...prev, step: 3 }))}
                                        className="w-full mt-4 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold transition-all"
                                    >
                                        Continue <ArrowRight size={14} />
                                    </button>
                                </div>
                            )}

                            {/* Step 3: Configuration */}
                            {enableModal.step === 3 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-hig-text-primary mb-1">Configure Workflow</h3>
                                    <p className="text-xs text-hig-text-secondary mb-4">
                                        Customize how this workflow runs.
                                    </p>
                                    <div className="space-y-3">
                                        {(() => {
                                            try {
                                                const schema = JSON.parse(enableModal.template!.inputSchema);
                                                if (!schema.properties) return <p className="text-xs text-hig-text-tertiary">No configuration needed.</p>;
                                                return Object.entries(schema.properties).map(([key, prop]: [string, any]) => (
                                                    <div key={key} className="p-3 rounded-lg bg-hig-bg-tertiary">
                                                        <label className="text-sm text-hig-text-primary mb-1 block">{prop.title || key}</label>
                                                        {prop.description && <p className="text-xs text-hig-text-secondary mb-2">{prop.description}</p>}
                                                        {prop.enum ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {prop.enum.map((opt: string) => (
                                                                    <button
                                                                        key={opt}
                                                                        onClick={() => setModalConfig(c => ({ ...c, [key]: opt }))}
                                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${modalConfig[key] === opt
                                                                            ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                                                                            : 'bg-hig-bg-secondary text-hig-text-secondary'
                                                                            }`}
                                                                    >
                                                                        {opt}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        ) : prop.type === 'boolean' ? (
                                                            <button
                                                                onClick={() => setModalConfig(c => ({ ...c, [key]: !c[key] }))}
                                                                className="flex-shrink-0"
                                                            >
                                                                {modalConfig[key]
                                                                    ? <ToggleRight size={28} className="text-hig-accent" />
                                                                    : <ToggleLeft size={28} className="text-hig-text-tertiary" />}
                                                            </button>
                                                        ) : prop.type === 'number' ? (
                                                            <input
                                                                type="number"
                                                                value={modalConfig[key] ?? prop.default ?? ''}
                                                                min={prop.minimum}
                                                                max={prop.maximum}
                                                                onChange={e => setModalConfig(c => ({ ...c, [key]: Number(e.target.value) }))}
                                                                className="w-full px-3 py-2 rounded-lg bg-hig-bg-secondary border border-hig-border text-sm text-hig-text-primary focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            />
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                value={modalConfig[key] ?? prop.default ?? ''}
                                                                placeholder={prop.default || ''}
                                                                onChange={e => setModalConfig(c => ({ ...c, [key]: e.target.value }))}
                                                                className="w-full px-3 py-2 rounded-lg bg-hig-bg-secondary border border-hig-border text-sm text-hig-text-primary focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            />
                                                        )}
                                                    </div>
                                                ));
                                            } catch { return <p className="text-xs text-hig-text-tertiary">No configuration needed.</p>; }
                                        })()}
                                    </div>

                                    <button
                                        onClick={() => enableWorkflow()}
                                        className="w-full mt-4 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold transition-all"
                                    >
                                        Enable & Run <Play size={14} />
                                    </button>
                                </div>
                            )}

                            {/* Step 4: First Run */}
                            {enableModal.step === 4 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-hig-text-primary mb-1">First Run</h3>
                                    {!modalRunResult ? (
                                        <div className="flex items-center justify-center py-12">
                                            <div className="text-center">
                                                <Loader2 size={24} className="animate-spin text-hig-accent mx-auto mb-3" />
                                                <p className="text-sm text-hig-text-secondary">Running your workflow for the first time…</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            {/* Status */}
                                            <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${modalRunResult.status === 'completed' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                                                }`}>
                                                {modalRunResult.status === 'completed' ? (
                                                    <CheckCircle size={16} className="text-emerald-400" />
                                                ) : (
                                                    <AlertTriangle size={16} className="text-amber-400" />
                                                )}
                                                <span className={`text-sm ${modalRunResult.status === 'completed' ? 'text-emerald-400' : 'text-amber-400'}`}>
                                                    {modalRunResult.status === 'completed' ? 'Workflow completed successfully!' : `Status: ${modalRunResult.status}`}
                                                </span>
                                            </div>

                                            {/* Artifacts */}
                                            {modalRunResult.artifacts && modalRunResult.artifacts.length > 0 && (
                                                <div className="mb-4">
                                                    <p className="text-xs font-semibold text-hig-text-secondary mb-2">Output</p>
                                                    {modalRunResult.artifacts.map(art => (
                                                        <div key={art.id} className="bg-hig-bg-tertiary rounded-lg p-3 mb-2">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <Eye size={12} className="text-hig-accent" />
                                                                <span className="text-xs font-medium text-hig-text-primary">{art.title}</span>
                                                            </div>
                                                            <pre className="text-xs text-hig-text-secondary whitespace-pre-wrap max-h-48 overflow-y-auto leading-relaxed">
                                                                {art.content}
                                                            </pre>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <button
                                                onClick={() => setEnableModal({ open: false, template: null, step: 1 })}
                                                className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold transition-all"
                                            >
                                                <Check size={14} /> Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════ */}
            {/* History Panel                           */}
            {/* ═════════════════════════════════════════ */}

            {historyPanel.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-hig-bg-primary border border-hig-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="sticky top-0 bg-hig-bg-primary border-b border-hig-border p-4 rounded-t-2xl flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-hig-text-primary flex items-center gap-2">
                                <History size={18} /> Run History
                            </h2>
                            <button onClick={() => setHistoryPanel({ open: false, workflowId: null })} className="p-1 rounded-lg hover:bg-hig-bg-tertiary">
                                <X size={18} className="text-hig-text-tertiary" />
                            </button>
                        </div>

                        <div className="p-4">
                            {historyLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 size={20} className="animate-spin text-hig-accent" />
                                </div>
                            ) : historyRuns.length === 0 ? (
                                <p className="text-sm text-hig-text-tertiary text-center py-8">No runs yet.</p>
                            ) : (
                                <div className="space-y-3">
                                    {historyRuns.map(run => {
                                        const s = STATUS_STYLES[run.status];
                                        const SIcon = s?.Icon || Clock;
                                        return (
                                            <div key={run.id} className="glass p-3">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className={`flex items-center gap-1.5 text-xs ${s?.color || ''}`}>
                                                        <SIcon size={12} className={run.status === 'running' ? 'animate-spin' : ''} />
                                                        {s?.label || run.status}
                                                    </span>
                                                    <span className="text-[10px] text-hig-text-tertiary">{formatDate(run.createdAt)}</span>
                                                </div>
                                                {run.error && (
                                                    <p className="text-xs text-red-400 mt-1">{run.error}</p>
                                                )}
                                                {run.artifacts && run.artifacts.length > 0 && (
                                                    <div className="mt-2">
                                                        {run.artifacts.map(art => (
                                                            <details key={art.id} className="bg-hig-bg-tertiary rounded-lg">
                                                                <summary className="px-3 py-2 text-xs font-medium text-hig-text-primary cursor-pointer flex items-center gap-1.5">
                                                                    <Eye size={10} className="text-hig-accent" /> {art.title}
                                                                </summary>
                                                                <pre className="px-3 pb-3 text-xs text-hig-text-secondary whitespace-pre-wrap max-h-40 overflow-y-auto">
                                                                    {art.content}
                                                                </pre>
                                                            </details>
                                                        ))}
                                                    </div>
                                                )}
                                                {run.status === 'needs_approval' && (
                                                    <div className="flex gap-2 mt-2">
                                                        <button
                                                            onClick={async () => {
                                                                await api.post(`/workflows/workflow-runs/${run.id}/approve`);
                                                                loadHistory(historyPanel.workflowId!);
                                                            }}
                                                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white"
                                                        >
                                                            <Check size={12} /> Approve
                                                        </button>
                                                        <button
                                                            onClick={async () => {
                                                                await api.post(`/workflows/workflow-runs/${run.id}/cancel`);
                                                                loadHistory(historyPanel.workflowId!);
                                                            }}
                                                            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 text-white"
                                                        >
                                                            <X size={12} /> Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════ */}
            {/* Settings Panel                          */}
            {/* ═════════════════════════════════════════ */}

            {settingsPanel.open && settingsPanel.workflow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-hig-bg-primary border border-hig-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="sticky top-0 bg-hig-bg-primary border-b border-hig-border p-4 rounded-t-2xl flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-hig-text-primary flex items-center gap-2">
                                <Settings size={18} /> Workflow Settings
                            </h2>
                            <button onClick={() => setSettingsPanel({ open: false, workflow: null })} className="p-1 rounded-lg hover:bg-hig-bg-tertiary">
                                <X size={18} className="text-hig-text-tertiary" />
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            {/* Name */}
                            <div>
                                <label className="text-xs font-medium text-hig-text-secondary mb-1 block">Workflow Name</label>
                                <input
                                    type="text"
                                    value={settingsPanel.workflow.name}
                                    onChange={e => setSettingsPanel(prev => ({
                                        ...prev,
                                        workflow: prev.workflow ? { ...prev.workflow, name: e.target.value } : null,
                                    }))}
                                    className="w-full px-3 py-2 rounded-lg bg-hig-bg-tertiary border border-hig-border text-sm text-hig-text-primary focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>

                            {/* Mode */}
                            <div>
                                <label className="text-xs font-medium text-hig-text-secondary mb-2 block">Run Mode</label>
                                <div className="flex gap-2">
                                    {['manual', 'scheduled', 'triggered'].map(mode => (
                                        <button
                                            key={mode}
                                            onClick={() => setSettingsPanel(prev => ({
                                                ...prev,
                                                workflow: prev.workflow ? { ...prev.workflow, mode } : null,
                                            }))}
                                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all capitalize ${settingsPanel.workflow.mode === mode
                                                ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                                                : 'bg-hig-bg-tertiary text-hig-text-secondary'
                                                }`}
                                        >
                                            {mode}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Schedule (if mode=scheduled) */}
                            {settingsPanel.workflow.mode === 'scheduled' && (
                                <div>
                                    <label className="text-xs font-medium text-hig-text-secondary mb-2 block">Schedule</label>
                                    <div className="flex flex-wrap gap-2">
                                        {['daily', 'weekly', 'every 4 hours', 'every 30 minutes'].map(sched => (
                                            <button
                                                key={sched}
                                                onClick={() => setSettingsPanel(prev => ({
                                                    ...prev,
                                                    workflow: prev.workflow ? { ...prev.workflow, schedule: sched } : null,
                                                }))}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${settingsPanel.workflow.schedule === sched
                                                    ? 'bg-hig-accent/10 text-hig-accent border border-hig-accent/20'
                                                    : 'bg-hig-bg-tertiary text-hig-text-secondary'
                                                    }`}
                                            >
                                                {sched}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Policy */}
                            <div>
                                <label className="text-xs font-medium text-hig-text-secondary mb-2 block">Boundaries</label>
                                {(() => {
                                    const policy: WorkflowPolicy = JSON.parse(settingsPanel.workflow.policy || '{}');
                                    return (
                                        <div className="space-y-2">
                                            <label className="flex items-center justify-between p-3 rounded-lg bg-hig-bg-tertiary">
                                                <span className="text-sm text-hig-text-primary">Review before actions</span>
                                                <button onClick={() => {
                                                    const updated = { ...policy, approvalRequired: !policy.approvalRequired };
                                                    setSettingsPanel(prev => ({
                                                        ...prev,
                                                        workflow: prev.workflow ? { ...prev.workflow, policy: JSON.stringify(updated) } : null,
                                                    }));
                                                }}>
                                                    {policy.approvalRequired
                                                        ? <ToggleRight size={28} className="text-hig-accent" />
                                                        : <ToggleLeft size={28} className="text-hig-text-tertiary" />}
                                                </button>
                                            </label>
                                            <label className="flex items-center justify-between p-3 rounded-lg bg-hig-bg-tertiary">
                                                <span className="text-sm text-hig-text-primary">Allow writing drafts</span>
                                                <button onClick={() => {
                                                    const updated = { ...policy, allowWrite: !policy.allowWrite };
                                                    setSettingsPanel(prev => ({
                                                        ...prev,
                                                        workflow: prev.workflow ? { ...prev.workflow, policy: JSON.stringify(updated) } : null,
                                                    }));
                                                }}>
                                                    {policy.allowWrite
                                                        ? <ToggleRight size={28} className="text-hig-accent" />
                                                        : <ToggleLeft size={28} className="text-hig-text-tertiary" />}
                                                </button>
                                            </label>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={updateWorkflowSettings}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium bg-hig-accent hover:opacity-88 text-hig-bg-primary font-bold transition-all"
                                >
                                    <Check size={14} /> Save Changes
                                </button>
                                <button
                                    onClick={async () => {
                                        if (settingsPanel.workflow) {
                                            await disableWorkflow(settingsPanel.workflow.id);
                                            setSettingsPanel({ open: false, workflow: null });
                                        }
                                    }}
                                    className="px-4 py-2.5 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                                >
                                    Disable
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
